const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Plantillas y Atajos de Productividad — Respuestas Rápidas",
    summary:
      "Las Respuestas Rápidas permiten estandarizar la atención al cliente y multiplicar la velocidad de respuesta de tu equipo. Configura atajos de teclado (ej: '/saludo', '/precios') en tres modalidades: Generales, Plantillas Meta Aprobadas e Interactivos, con disponibilidad global o por línea telefónica.",
  },
  sections: [
    {
      id: "tipos_respuestas",
      title: "1. Los 3 Tipos de Respuestas Rápidas",
      description:
        "Diferencias y casos de uso entre las tres modalidades del sistema.",
      items: [
        {
          id: "tipo_general",
          title: "General (Universal y Ágil)",
          definition:
            "Respuestas estándar compuestas por bloques secuenciales de texto, notas de voz grabadas (micrófono verde), imágenes, documentos, audios o videos. Ideales para que cualquier agente las invoque rápidamente en cualquier chat.",
        },
        {
          id: "tipo_plantilla_aprobada",
          title: "Plantilla Aprobada (Meta API Oficial / HSM)",
          definition:
            "Mensajes pre-aprobados formalmente por Meta en el Administrador de WhatsApp Business. Se cargan y sincronizan automáticamente según el número oficial elegido. IMPORTANTE: Solo aplican para conexiones con Meta API Oficial; no funcionan en conexiones por código QR (Baileys).",
        },
        {
          id: "tipo_interactivos",
          title: "Interactivos (Botones, Listas y Carruseles)",
          definition:
            "Respuestas dinámicas con elementos visuales accionables: botones de respuesta rápida (Quick Reply), botones Call to Action (enlace URL o llamada telefónica), listas de opciones desplegables y carruseles de tarjetas con botones independientes.",
        },
      ],
    },
    {
      id: "modalidad_global_conexion",
      title: "2. Modalidad Global vs Asignación por Conexión",
      description:
        "Alcance de disponibilidad de la respuesta rápida en tus canales.",
      items: [
        {
          id: "modalidad_global",
          title: "Ninguna (Global) — Modalidad Recomendada",
          definition:
            "La respuesta rápida no se vincula a ningún número de teléfono en particular. Queda disponible de inmediato para todos los usuarios y agentes desde cualquier línea o canal de la empresa. Es la configuración estándar y más eficiente.",
        },
        {
          id: "modalidad_especifica",
          title: "Asignación por Conexión Específica",
          definition:
            "Restringe la respuesta rápida para que solo se muestre al atender chats de ese número telefónico. Es de uso obligatorio en las Plantillas Aprobadas de Meta (ya que cada plantilla le pertenece a una cuenta WABA oficial específica).",
        },
      ],
    },
    {
      id: "configuracion_atajos",
      title: "3. Creación de Atajos y Plantillas",
      description:
        "Cómo estructurar atajos eficientes para los agentes de atención.",
      items: [
        {
          id: "codigo_atajo",
          title: "Código del Atajo (Shortcode)",
          definition:
            "Palabra clave breve que el operador escribe en el chat precedida de barra diagonal (ej: 'gracias', 'cuenta').",
          importance:
            "Debe ser corto y fácil de recordar para que el operador lo invoque rápidamente mientras atiende clientes.",
        },
        {
          id: "cuerpo_mensaje",
          title: "Contenido del Mensaje",
          definition:
            "Texto completo redactado con las políticas oficiales de la empresa, listo para enviarse sin errores ortográficos.",
        },
      ],
    },
    {
      id: "variables_dinamicas",
      title: "4. Variables Dinámicas de Personalización",
      description:
        "Etiquetas que se sustituyen automáticamente por los datos reales del cliente al momento del envío.",
      items: [
        {
          id: "var_name",
          title: "{name} — Nombre del Cliente",
          definition:
            "Inserta automáticamente el nombre guardado del contacto para un trato cercano y profesional.",
        },
        {
          id: "var_protocol",
          title: "{protocol} — Número de Ticket / Protocolo",
          definition:
            "Inserta el correlativo único de atención para control y seguimiento formal.",
        },
      ],
    },
    {
      id: "multimedia_adjuntos",
      title: "5. Audios Como Notas de Voz y Adjuntos",
      description:
        "Potencia tus respuestas con notas de voz grabadas y catálogos.",
      items: [
        {
          id: "audio_ptt",
          title: "Audio Enviado como Nota de Voz Directa",
          definition:
            "Al adjuntar un archivo de audio (.mp3 o .ogg), BotMix lo envía como nota de voz grabada en el momento (con micrófono verde), generando máxima cercanía humana.",
        },
        {
          id: "archivos_catalogo",
          title: "PDFs y Catálogos Comerciales",
          definition:
            "Adjunta listas de precios o fichas técnicas para que se despachen junto con el mensaje de texto explicativo.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "Official BotMix Guide",
    title: "Productivity Templates & Shortcuts — Quick Replies",
    summary:
      "Quick Replies standardize customer support and multiply your team's response speed. Configure keyboard shortcuts (e.g. '/greeting', '/pricing') across three formats: General, Meta Approved Templates, and Interactive Messages, with global or line-specific availability.",
  },
  sections: [
    {
      id: "tipos_respuestas",
      title: "1. The 3 Types of Quick Replies",
      description:
        "Key differences and operational use cases among the three modalities.",
      items: [
        {
          id: "tipo_general",
          title: "General (Universal & Agile)",
          definition:
            "Standard canned responses composed of sequential text blocks, recorded voice notes (green mic), images, documents, audio, or video. Ideal for any agent to trigger instantly in any customer conversation.",
        },
        {
          id: "tipo_plantilla_aprobada",
          title: "Approved Template (Meta Official API / HSM)",
          definition:
            "Pre-approved messages formally registered with Meta via WhatsApp Business Manager. Automatically loaded and synchronized according to the chosen official number. IMPORTANT: Only applies to Meta Official API connections; not supported on QR code (Baileys) lines.",
        },
        {
          id: "tipo_interactivos",
          title: "Interactive (Buttons, Lists & Carousels)",
          definition:
            "Actionable responses featuring interactive elements: Quick Reply buttons, Call to Action buttons (URL or phone call), select dropdown menus, and multi-card interactive carousels.",
        },
      ],
    },
    {
      id: "modalidad_global_conexion",
      title: "2. Global Mode vs Dedicated Connection Assignment",
      description:
        "Determining the availability scope of canned responses across your channels.",
      items: [
        {
          id: "modalidad_global",
          title: "None (Global) — Recommended Default",
          definition:
            "The quick reply is not tied to any individual telephone number. It remains immediately available for all agents across every company channel. This is the standard, best-practice configuration.",
        },
        {
          id: "modalidad_especifica",
          title: "Specific Connection Assignment",
          definition:
            "Restricts the quick reply exclusively to tickets serviced through the specified number. Mandatory for Meta Approved Templates (since each template belongs to a distinct official WABA account).",
        },
      ],
    },
    {
      id: "configuracion_atajos",
      title: "3. Shortcut and Template Setup",
      description:
        "How to design efficient shortcuts for customer support agents.",
      items: [
        {
          id: "codigo_atajo",
          title: "Shortcut Code (Shortcode)",
          definition:
            "Short keyword typed by the operator in chat preceded by a forward slash (e.g. 'thanks', 'bank').",
          importance:
            "Should be concise and easy to memorize so agents can invoke it effortlessly while handling tickets.",
        },
        {
          id: "cuerpo_mensaje",
          title: "Message Content",
          definition:
            "Full text formulated according to company guidelines, ready to dispatch without typos.",
        },
      ],
    },
    {
      id: "variables_dinamicas",
      title: "4. Dynamic Personalization Variables",
      description:
        "Smart tags automatically substituted by actual customer data at the moment of sending.",
      items: [
        {
          id: "var_name",
          title: "{name} — Customer Name",
          definition:
            "Automatically inserts the saved contact name for an approachable, professional conversation.",
        },
        {
          id: "var_protocol",
          title: "{protocol} — Ticket Protocol Number",
          definition:
            "Inserts the unique service tracking code for formal logging and accountability.",
        },
      ],
    },
    {
      id: "multimedia_adjuntos",
      title: "5. Voice Notes and Media Attachments",
      description:
        "Enhance your canned replies with native voice recordings and catalogs.",
      items: [
        {
          id: "audio_ptt",
          title: "Audio Delivered as Direct Voice Note (PTT)",
          definition:
            "When attaching an audio file (.mp3 or .ogg), BotMix dispatches it as an instant voice note (green microphone), providing authentic human closeness.",
        },
        {
          id: "archivos_catalogo",
          title: "PDFs and Commercial Brochures",
          definition:
            "Attach price lists or technical spec sheets to be delivered together with the explanatory message.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Modelos e Atalhos de Produtividade — Respostas Rápidas",
    summary:
      "As Respostas Rápidas padronizam o atendimento ao cliente e multiplicam a agilidade da sua equipe. Configure atalhos de teclado (ex: '/saudacao', '/valores') em três modalidades: Gerais, Modelos Aprovados Meta e Interativos, com alcance global ou por linha telefônica.",
  },
  sections: [
    {
      id: "tipos_respuestas",
      title: "1. Os 3 Tipos de Respostas Rápidas",
      description:
        "Diferenças operacionais entre as três modalidades do sistema.",
      items: [
        {
          id: "tipo_general",
          title: "Geral (Universal e Ágil)",
          definition:
            "Respostas padrão compostas por blocos sequenciais de texto, notas de voz gravadas (microfone verde), imagens, documentos, áudios ou vídeos. Ideais para uso rápido por qualquer atendente em qualquer conversa.",
        },
        {
          id: "tipo_plantilla_aprobada",
          title: "Modelo Aprovado (Meta API Oficial / HSM)",
          definition:
            "Mensagens pré-aprovadas oficialmente pela Meta no Gerenciador do WhatsApp Business. Carregadas e sincronizadas automaticamente conforme o número oficial escolhido. IMPORTANTE: Aplicam-se apenas a conexões com Meta API Oficial; não funcionam em conexões por QR code (Baileys).",
        },
        {
          id: "tipo_interactivos",
          title: "Interativos (Botões, Listas e Carrosséis)",
          definition:
            "Respostas acionáveis com elementos visuais: botões de resposta rápida (Quick Reply), botões Call to Action (link URL ou chamada telefônica), menus de opções suspensas e carrosséis de cartões independentes.",
        },
      ],
    },
    {
      id: "modalidad_global_conexion",
      title: "2. Modalidade Global vs Conexão Específica",
      description:
        "Definição do escopo de disponibilidade das respostas nos canais.",
      items: [
        {
          id: "modalidad_global",
          title: "Nenhuma (Global) — Modalidade Recomendada",
          definition:
            "A resposta rápida não é vinculada a nenhum número específico. Fica imediatamente disponível para todos os operadores em qualquer canal da empresa. É a configuração padrão recomendada.",
        },
        {
          id: "modalidad_especifica",
          title: "Atribuição por Conexão Específica",
          definition:
            "Restringe a resposta rápida aos atendimentos realizados através do número selecionado. Obrigatória para Modelos Aprovados da Meta (pois cada modelo pertence a uma conta WABA oficial específica).",
        },
      ],
    },
    {
      id: "configuracion_atajos",
      title: "3. Criação de Atalhos e Modelos",
      description:
        "Como estruturar atalhos eficientes para os atendentes.",
      items: [
        {
          id: "codigo_atajo",
          title: "Código do Atalho (Shortcode)",
          definition:
            "Palavra-chave curta digitada no chat antecedida por barra (ex: 'obrigado', 'conta').",
          importance:
            "Deve ser breve e fácil de memorizar para que o operador o invoque rapidamente durante os atendimentos.",
        },
        {
          id: "cuerpo_mensaje",
          title: "Conteúdo da Mensagem",
          definition:
            "Texto completo alinhado às diretrizes oficiais da empresa, pronto para envio sem erros gramaticais.",
        },
      ],
    },
    {
      id: "variables_dinamicas",
      title: "4. Variáveis Dinâmicas de Personalização",
      description:
        "Tags substituídas automaticamente pelos dados reais do cliente no momento do envio.",
      items: [
        {
          id: "var_name",
          title: "{name} — Nome do Cliente",
          definition:
            "Insere automaticamente o nome salvo do contato para um tratamento caloroso e profissional.",
        },
        {
          id: "var_protocol",
          title: "{protocol} — Número do Ticket / Protocolo",
          definition:
            "Insere o número sequencial exclusivo de atendimento para controle e histórico formal.",
        },
      ],
    },
    {
      id: "multimedia_adjuntos",
      title: "5. Áudios como Mensagem de Voz e Anexos",
      description:
        "Aprimore suas respostas com notas de voz gravadas e catálogos.",
      items: [
        {
          id: "audio_ptt",
          title: "Áudio Enviado como Mensagem de Voz Direta",
          definition:
            "Ao anexar um arquivo de áudio (.mp3 ou .ogg), o BotMix o envia como nota de voz gravada na hora (com microfone verde), gerando máxima conexão humana.",
        },
        {
          id: "archivos_catalogo",
          title: "PDFs e Catálogos Comerciais",
          definition:
            "Anexe tabelas de preços ou fichas técnicas para envio conjunto com a mensagem explicativa.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Modèles et Raccourcis de Productivité — Réponses Rapides",
    summary:
      "Les Réponses Rapides standardisent le service client et décuplent la réactivité de votre équipe. Configurez des raccourcis clavier (ex: '/salut', '/tarifs') selon trois modalités : Général, Modèles Aprobés Meta et Interactifs, avec portée globale ou par ligne dédiée.",
  },
  sections: [
    {
      id: "tipos_respuestas",
      title: "1. Les 3 Formats de Réponses Rapides",
      description:
        "Distinctions clés et cas d'usage entre les trois modes du système.",
      items: [
        {
          id: "tipo_general",
          title: "Général (Universel et Réactif)",
          definition:
            "Réponses prêtes à l'emploi composées de blocs textuels, de mémos vocaux directs (micro vert), d'images, de documents, d'audios ou de vidéos. Idéales pour une utilisation agile par tout conseiller sur n'importe quel canal.",
        },
        {
          id: "tipo_plantilla_aprobada",
          title: "Modèle Approuvé (Meta API Officielle / HSM)",
          definition:
            "Messages formellement pré-approuvés par Meta via le gestionnaire WhatsApp Business. Chargés et synchronisés automatiquement selon le numéro officiel choisi. IMPORTANT : Exclusivement réservé aux connexions Meta API Officielle ; non compatible avec les lignes QR code (Baileys).",
        },
        {
          id: "tipo_interactivos",
          title: "Interactifs (Boutons, Listes et Carrousels)",
          definition:
            "Réponses dynamiques intégrant des composants interactifs : boutons de réponse rapide (Quick Reply), boutons Call to Action (lien URL ou appel téléphonique), menus déroulants et carrousels de fiches produits.",
        },
      ],
    },
    {
      id: "modalidad_global_conexion",
      title: "2. Portée Globale vs Affectation par Numéro Dédié",
      description:
        "Définition du niveau de disponibilité des modèles sur vos canaux.",
      items: [
        {
          id: "modalidad_global",
          title: "Aucune (Globale) — Recommandé par Défaut",
          definition:
            "Le modèle n'est rattaché à aucun numéro spécifique. Il est utilisable immédiatement par l'ensemble des agents sur tous les canaux de l'entreprise. C'est la configuration standard la plus pratique.",
        },
        {
          id: "modalidad_especifica",
          title: "Affectation à un Canal Spécifique",
          definition:
            "Limite la réponse rapide aux échanges traités sur la ligne téléphonique sélectionnée. Obligatoire pour les Modèles Approuvés de Meta (chaque modèle appartenant à un compte WABA officiel précis).",
        },
      ],
    },
    {
      id: "configuracion_atajos",
      title: "3. Configuration des Raccourcis et Modèles",
      description:
        "Comment concevoir des raccourcis performants pour les conseillers.",
      items: [
        {
          id: "codigo_atajo",
          title: "Code de Raccourci (Shortcode)",
          definition:
            "Mot-clé bref saisi dans la discussion précédé d'une barre oblique (ex: 'merci', 'devis').",
          importance:
            "Doit être court et facile à mémoriser pour que l'agent puisse l'appeler instantanément pendant les échanges.",
        },
        {
          id: "cuerpo_mensaje",
          title: "Contenu du Message",
          definition:
            "Texte intégral rédigé selon les chartes officielles de l'entreprise, prêt à l'envoi sans coquille.",
        },
      ],
    },
    {
      id: "variables_dinamicas",
      title: "4. Variables Dynamiques de Personnalisation",
      description:
        "Balises intelligentes automatiquement remplacées par les données réelles du client à l'envoi.",
      items: [
        {
          id: "var_name",
          title: "{name} — Nom du Client",
          definition:
            "Insère automatiquement le nom enregistré du contact pour une approche humaine et soignée.",
        },
        {
          id: "var_protocol",
          title: "{protocol} — Numéro de Ticket / Protocole",
          definition:
            "Insère l'identifiant séquentiel de suivi pour la traçabilité administrative.",
        },
      ],
    },
    {
      id: "multimedia_adjuntos",
      title: "5. Notes Vocales et Documents Joints",
      description:
        "Enrichissez vos modèles de réponses avec des mémos vocaux et des brochures.",
      items: [
        {
          id: "audio_ptt",
          title: "Audio Expédié comme Note Vocale Directe",
          definition:
            "En joignant un fichier audio (.mp3 ou .ogg), BotMix l'expédie comme une note vocale enregistrée sur le vif (micro vert), favorisant une proximité authentique.",
        },
        {
          id: "archivos_catalogo",
          title: "PDFs et Plaquettes Commerciales",
          definition:
            "Joignez grilles tarifaires ou fiches descriptives pour les délivrer en complément de l'explication textuelle.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "الدليل الرسمي لـ BotMix",
    title: "القوالب واختصارات الإنتاجية — الردود السريعة",
    summary:
      "تتيح الردود السريعة توحيد معايير خدمة العملاء ومضاعفة سرعة استجابة فريقك. قم بتهيئة اختصارات لوحة المفاتيح (مثل: '/ترحيب'، '/اسعار') عبر ثلاثة أنماط: عام، قوالب ميتا المعتمدة، والرسائل التفاعلية، مع توفر شامل أو مخصص لرقم معين.",
  },
  sections: [
    {
      id: "tipos_respuestas",
      title: "1. الأنواع الثلاثة للردود السريعة",
      description:
        "الفروق التشغيلية وحالات الاستخدام بين الأنماط الثلاثة في النظام.",
      items: [
        {
          id: "tipo_general",
          title: "عام (شامل ومرن)",
          definition:
            "ردود قياسية تتألف من كتل نصوص متسلسلة، وملاحظات صوتية مسجلة (ميكروفون أخضر)، وصور، ومستندات، وملفات صوتية أو فيديو. مثالية لاستدعائها فوراً بواسطة أي موظف في أي محادثة.",
        },
        {
          id: "tipo_plantilla_aprobada",
          title: "قالب معتمد (Meta API الرسمي / HSM)",
          definition:
            "رسائل معتمدة رسمياً من قِبل شركة Meta عبر مدير أعمال واتساب. يتم تحميلها ومزامنتها تلقائياً بناءً على الرقم الرسمي المختار. تنبيه هام: تنطبق حصرياً على اتصالات Meta API الرسمية ولا تعمل على أرقام الباركود (Baileys/QR).",
        },
        {
          id: "tipo_interactivos",
          title: "تفاعلي (أزرار، قوائم وشرائح عرض)",
          definition:
            "ردود غنية بعناصر تفاعلية قابلة للنقر: أزرار الرد السريع (Quick Reply)، أزرار الحث على اتخاذ إجراء (رابط URL أو اتصال هاتفي)، قوائم الخيارات المنسدلة، وشرائح الكاروسيل التفاعلية.",
        },
      ],
    },
    {
      id: "modalidad_global_conexion",
      title: "2. النمط الشامل مقابل التخصيص لقناة محددة",
      description:
        "تحديد نطاق إتاحة الرد السريع عبر قنوات الاتصال بالمؤسسة.",
      items: [
        {
          id: "modalidad_global",
          title: "بلا تخصيص (شامل - Global) — النمط الموصى به",
          definition:
            "لا يتم ربط الرد السريع برقم هاتف معين، مما يجعله متاحاً فوراً لجميع الموظفين عبر كافة خطوط وقنوات المؤسسة. هذا هو الإجراء القياسي والأكثر فاعلية.",
        },
        {
          id: "modalidad_especifica",
          title: "تخصيص لرقم اتصال محدد",
          definition:
            "يقصر ظهور الرد السريع على المحادثات الجارية عبر ذلك الرقم الهاتفي فقط. وهو إلزامي عند استخدام قوالب Meta المعتمدة (نظراً لأن كل قالب يرتبط بحساب WABA رسمي مستقل).",
        },
      ],
    },
    {
      id: "configuracion_atajos",
      title: "3. إنشاء الاختصارات والقوالب",
      description:
        "كيفية تنظيم اختصارات فعالة وسلسة لوكلاء الخدمة.",
      items: [
        {
          id: "codigo_atajo",
          title: "رمز الاختصار (Shortcode)",
          definition:
            "كلمة مفتاحية موجزة يكتبها الموظف في الدردشة مسبوقة بشرطة مائلة (مثل: 'شكرا'، 'حساب').",
          importance:
            "يجب أن تكون قصيرة وسهلة التذكر ليتمكن الموظف من استدعائها بسرعة أثناء خدمة العملاء.",
        },
        {
          id: "cuerpo_mensaje",
          title: "محتوى الرسالة",
          definition:
            "نص متكامل مصاغ وفقاً للسياسات الرسمية للمؤسسة، جاهز للإرسال دون أخطاء لغوية.",
        },
      ],
    },
    {
      id: "variables_dinamicas",
      title: "4. المتغيرات الديناميكية المخصصة",
      description:
        "علامات برمجية يتم استبدالها تلقائياً ببيانات العميل الفعلية وقت الإرسال.",
      items: [
        {
          id: "var_name",
          title: "{name} — اسم العميل",
          definition:
            "يدرج تلقائياً اسم جهة الاتصال المحفوظ لتعامل ودي واحترافي.",
        },
        {
          id: "var_protocol",
          title: "{protocol} — رقم التذكرة / البروتوكول",
          definition:
            "يدرج الرقم التسلسلي الفريد للخدمة لأغراض الرقابة والمتابعة الرسمية.",
        },
      ],
    },
    {
      id: "multimedia_adjuntos",
      title: "5. المقاطع الصوتية كرسائل صوتية والمرفقات",
      description:
        "عزز ردودك بملاحظات صوتية مسجلة وكتالوجات تفصيلية.",
      items: [
        {
          id: "audio_ptt",
          title: "إرسال الصوت كرسالة صوتية مباشرة (PTT)",
          definition:
            "عند إرفاق ملف صوتي (.mp3 أو .ogg)، يرسله BotMix كرسالة صوتية مسجلة في التو (بميكروفون أخضر)، مما يوفر أقصى درجات التقارب الإنساني.",
        },
        {
          id: "archivos_catalogo",
          title: "ملفات PDF والكتالوجات التجارية",
          definition:
            "أرفق قوائم الأسعار أو الكتيبات التعريفية ليتم إرسالها مصاحبة للرسالة التوضيحية.",
        },
      ],
    },
  ],
};

export const QUICK_MESSAGES_HELP_DATA = {
  es,
  en,
  pt,
  fr,
  ar,
};
